import { BaseNumber, CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import { stakingRewardsAbi } from '@/config/abis/stakingRewardsAbi'
import { TokenRepository } from '@/domain/token-repository/TokenRepository'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

interface GetFarmContractDataParams {
  farmAddress: CheckedAddress
  tokenRepository: TokenRepository
  wagmiConfig: Config
  chainId: number
  account: CheckedAddress | undefined
}

interface GetFarmContractDataResult {
  farmAddress: CheckedAddress
  rewardToken: Token
  stakingToken: Token
  rewardRate: NormalizedNumber
  periodFinish: number
  totalSupply: NormalizedNumber
  earned: NormalizedNumber
  staked: NormalizedNumber
  earnedTimestamp: number
}

export async function getFarmContractData({
  farmAddress,
  tokenRepository,
  wagmiConfig,
  chainId,
  account,
}: GetFarmContractDataParams): Promise<GetFarmContractDataResult> {
  function getStaked(): Promise<bigint> {
    if (!account) {
      return Promise.resolve(0n)
    }

    const res = readContract(wagmiConfig, {
      address: farmAddress,
      abi: stakingRewardsAbi,
      functionName: 'balanceOf',
      args: [account],
      chainId,
    })

    return res
  }

  function getEarned(): Promise<bigint> {
    if (!account) {
      return Promise.resolve(0n)
    }

    const res = readContract(wagmiConfig, {
      address: farmAddress,
      abi: stakingRewardsAbi,
      functionName: 'earned',
      args: [account],
      chainId,
    })

    return res
  }

  const [_staked, _earned, _rewardRate, _earnedTimestamp, _periodFinish, _totalSupply] = await Promise.all([
    getStaked(),
    getEarned(),
    readContract(wagmiConfig, {
      address: farmAddress,
      abi: stakingRewardsAbi,
      functionName: 'rewardRate',
      chainId,
    }),
    readContract(wagmiConfig, {
      address: farmAddress,
      abi: stakingRewardsAbi,
      functionName: 'lastTimeRewardApplicable',
      chainId,
    }),
    readContract(wagmiConfig, {
      address: farmAddress,
      abi: stakingRewardsAbi,
      functionName: 'periodFinish',
      chainId,
    }),
    readContract(wagmiConfig, {
      address: farmAddress,
      abi: stakingRewardsAbi,
      functionName: 'totalSupply',
      chainId,
    }),
  ])

  const rewardToken = tokenRepository.findOneBySymbol(TokenSymbol('SPK'))
  const stakingToken = tokenRepository.findOneBySymbol(TokenSymbol('USDS'))
  const rewardRate = rewardToken.fromBaseUnit(BaseNumber(_rewardRate))
  const periodFinish = Number(_periodFinish)
  const totalSupply = stakingToken.fromBaseUnit(BaseNumber(_totalSupply))
  const earned = rewardToken.fromBaseUnit(BaseNumber(_earned))
  const staked = stakingToken.fromBaseUnit(BaseNumber(_staked))
  const earnedTimestamp = Number(_earnedTimestamp)

  return {
    farmAddress,
    rewardToken,
    stakingToken,
    rewardRate,
    periodFinish,
    totalSupply,
    earned,
    staked,
    earnedTimestamp,
  }
}
